/**
 * PhotoGallery — Weidner Classic
 *
 * Tower 12 gallery page (https://www.tower12.com/gallery): filter tabs
 * (ALL / Video / Community Amenities / Apartment Amenities) above a
 * 3-column media grid with lightbox. Videos come from `virtual_tours`;
 * community / apartment photos are filtered from `photo_gallery` by
 * title (with a loose fallback when titles are not exact).
 *
 * Replaces the rise GalleryApartments + GallerySeparator +
 * GalleryCommunity stack on the gallery page.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type TouchEvent as ReactTouchEvent,
} from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  EditableCollectionBinding,
  EditableToken,
  useContentToken,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Types
// ============================================================================

type GalleryTab = 'all' | 'video' | 'community' | 'apartment';

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  link?: string | null;
}

interface VirtualTour {
  virtual_tour_thumbnail?: string | null;
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
  virtual_tour_type?: string | null;
  virtual_tour_description?: string | null;
}

interface GalleryMediaItem {
  key: string;
  image: string;
  alt: string;
  title: string;
  videoUrl?: string;
  category: 'video' | 'community' | 'apartment' | 'other';
}

// ============================================================================
// Helpers
// ============================================================================

function photoSrc(photo: GalleryPhoto): string {
  return (photo.image || photo.original_url || '').trim();
}

function titleOf(photo: GalleryPhoto): string {
  return (photo.title || '').trim();
}

function isCommunityTitle(title: string): boolean {
  const t = title.toLowerCase();
  return t === 'community' || t.includes('community');
}

function isApartmentTitle(title: string): boolean {
  const t = title.toLowerCase();
  if (isCommunityTitle(title)) return false;
  return t === 'apartment' || t.includes('apartment') || t.includes('interior');
}

function isVideoTitle(title: string): boolean {
  return title.toLowerCase().includes('video');
}

function looksLikeVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url) || /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
}

function toEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      const id = u.pathname.replace(/^\//, '');
      return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : url;
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : url;
    }
  } catch {
    /* keep original */
  }
  return url;
}

function isDirectVideo(url: string): boolean {
  return /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
}

function mapPhoto(photo: GalleryPhoto, index: number): GalleryMediaItem | null {
  const image = photoSrc(photo);
  if (!image) return null;
  const title = titleOf(photo);
  const link = (photo.link || '').trim();
  const videoUrl = link && looksLikeVideoUrl(link) ? link : undefined;
  let category: GalleryMediaItem['category'] = 'other';
  if (videoUrl || isVideoTitle(title)) category = 'video';
  else if (isCommunityTitle(title)) category = 'community';
  else if (isApartmentTitle(title)) category = 'apartment';

  return {
    key: `photo-${index}-${image}`,
    image,
    alt: photo.alt_text || title || 'Property photo',
    title: title || 'Photo',
    videoUrl,
    category,
  };
}

function mapTour(tour: VirtualTour, index: number): GalleryMediaItem | null {
  const videoUrl = (tour.virtual_tour_url || '').trim();
  const image = (tour.virtual_tour_thumbnail || '').trim();
  if (!videoUrl && !image) return null;
  return {
    key: `tour-${index}-${videoUrl || image}`,
    image: image || videoUrl,
    alt: tour.virtual_tour_title || 'Video',
    title: tour.virtual_tour_title || 'Video',
    videoUrl: videoUrl || undefined,
    category: 'video',
  };
}

// ============================================================================
// Lightbox
// ============================================================================

function GalleryLightbox({
  items,
  selectedIndex,
  onClose,
  onChange,
}: {
  items: GalleryMediaItem[];
  selectedIndex: number | null;
  onClose: () => void;
  onChange: (index: number | null) => void;
}) {
  const total = items.length;
  const selected = selectedIndex !== null ? items[selectedIndex] : null;

  const goNext = useCallback(
    (e?: ReactMouseEvent | ReactKeyboardEvent) => {
      e?.stopPropagation();
      if (selectedIndex === null || total === 0) return;
      onChange((selectedIndex + 1) % total);
    },
    [onChange, selectedIndex, total],
  );

  const goPrev = useCallback(
    (e?: ReactMouseEvent | ReactKeyboardEvent) => {
      e?.stopPropagation();
      if (selectedIndex === null || total === 0) return;
      onChange((selectedIndex - 1 + total) % total);
    },
    [onChange, selectedIndex, total],
  );

  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedIndex, goNext, goPrev, onClose]);

  const touchStartX = useRef<number | null>(null);
  const didSwipe = useRef(false);

  const handleTouchStart = useCallback((e: ReactTouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    didSwipe.current = false;
  }, []);

  const handleTouchEnd = useCallback(
    (e: ReactTouchEvent) => {
      if (touchStartX.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(dx) < 40) return;
      didSwipe.current = true;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  if (!selected) return null;

  const videoUrl = selected.videoUrl;

  return (
    <div
      className="photo-gallery__lightbox"
      onClick={() => {
        if (didSwipe.current) {
          didSwipe.current = false;
          return;
        }
        onClose();
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-label="Gallery lightbox"
    >
      <button
        type="button"
        className="photo-gallery__lightbox-close"
        aria-label="Close lightbox"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        &times;
      </button>

      {total > 1 ? (
        <button
          type="button"
          className="photo-gallery__lightbox-arrow photo-gallery__lightbox-arrow--prev"
          aria-label="Previous"
          onClick={goPrev}
        >
          &#8249;
        </button>
      ) : null}

      <div
        className="photo-gallery__lightbox-media"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {videoUrl ? (
          isDirectVideo(videoUrl) ? (
            <video
              className="photo-gallery__lightbox-video"
              src={videoUrl}
              controls
              autoPlay
              playsInline
            />
          ) : (
            <iframe
              className="photo-gallery__lightbox-iframe"
              src={toEmbedUrl(videoUrl)}
              title={selected.title || 'Video'}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          )
        ) : (
          <VibesitesImage
            url={selected.image}
            alt={selected.alt}
            className="photo-gallery__lightbox-img"
            sizes="90vw"
          />
        )}
      </div>

      {total > 1 ? (
        <button
          type="button"
          className="photo-gallery__lightbox-arrow photo-gallery__lightbox-arrow--next"
          aria-label="Next"
          onClick={goNext}
        >
          &#8250;
        </button>
      ) : null}

      {total > 1 ? (
        <div className="photo-gallery__lightbox-counter" aria-live="polite">
          {(selectedIndex ?? 0) + 1} / {total}
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function PhotoGallery() {
  const tabAll = useContentToken('photogallery.tabAll');
  const tabVideo = useContentToken('photogallery.tabVideo');
  const tabCommunity = useContentToken('photogallery.tabCommunity');
  const tabApartment = useContentToken('photogallery.tabApartment');
  const tabLabels: Record<GalleryTab, string> = {
    all: tabAll || 'ALL',
    video: tabVideo || 'Video',
    community: tabCommunity || 'Community Amenities',
    apartment: tabApartment || 'Apartment Amenities',
  };

  const allPhotos = useCollectionBinding<GalleryPhoto>('photogallery.images', {
    collection: 'photo_gallery',
  });
  const communityPhotos = useCollectionBinding<GalleryPhoto>('photogallery.community', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
  });
  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('photogallery.apartment', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
  });
  const tours = useCollectionBinding<VirtualTour>('photogallery.videos', {
    collection: 'virtual_tours',
  });

  const { videoItems, communityItems, apartmentItems, allItems } = useMemo(() => {
    const fromTours = (tours ?? [])
      .map((t, i) => mapTour(t, i))
      .filter((x): x is GalleryMediaItem => !!x);

    const mappedAll = (allPhotos ?? [])
      .map((p, i) => mapPhoto(p, i))
      .filter((x): x is GalleryMediaItem => !!x);

    const fromPhotosVideo = mappedAll.filter((p) => p.category === 'video');
    const video = [...fromTours, ...fromPhotosVideo];

    let community = (communityPhotos ?? [])
      .map((p, i) => mapPhoto(p, i))
      .filter((x): x is GalleryMediaItem => !!x && x.category !== 'video');
    if (community.length === 0) {
      community = mappedAll.filter((p) => p.category === 'community');
    }

    let apartment = (apartmentPhotos ?? [])
      .map((p, i) => mapPhoto(p, i))
      .filter((x): x is GalleryMediaItem => !!x && x.category !== 'video');
    if (apartment.length === 0) {
      apartment = mappedAll.filter((p) => p.category === 'apartment');
    }

    // When categories are empty, fall back so the page still shows photos.
    if (community.length === 0 && apartment.length === 0) {
      const other = mappedAll.filter((p) => p.category === 'other' || p.category === 'apartment');
      apartment = other.length > 0 ? other : mappedAll.filter((p) => p.category !== 'video');
    }

    const all = [...video, ...community, ...apartment];
    // Deduplicate by image URL while preserving order.
    const seen = new Set<string>();
    const dedupedAll = all.filter((item) => {
      const id = item.videoUrl || item.image;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    return {
      videoItems: video,
      communityItems: community,
      apartmentItems: apartment,
      allItems: dedupedAll,
    };
  }, [allPhotos, communityPhotos, apartmentPhotos, tours]);

  const [activeTab, setActiveTab] = useState<GalleryTab>('all');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const visibleItems = useMemo(() => {
    switch (activeTab) {
      case 'video':
        return videoItems;
      case 'community':
        return communityItems;
      case 'apartment':
        return apartmentItems;
      default:
        return allItems;
    }
  }, [activeTab, videoItems, communityItems, apartmentItems, allItems]);

  // Reset lightbox when the filtered set changes.
  useEffect(() => {
    setSelectedIndex(null);
  }, [activeTab]);

  return (
    <section className="photo-gallery bg-background">
      <div className="photo-gallery__inner">
        <div className="photo-gallery__tabs" role="tablist" aria-label="Gallery filters">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'all'}
            className={`photo-gallery__tab${activeTab === 'all' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            <EditableToken path="photogallery.tabAll" as="span">
              {tabLabels.all}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'video'}
            className={`photo-gallery__tab${activeTab === 'video' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('video')}
          >
            <EditableToken path="photogallery.tabVideo" as="span">
              {tabLabels.video}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'community'}
            className={`photo-gallery__tab${activeTab === 'community' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('community')}
          >
            <EditableToken path="photogallery.tabCommunity" as="span">
              {tabLabels.community}
            </EditableToken>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'apartment'}
            className={`photo-gallery__tab${activeTab === 'apartment' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('apartment')}
          >
            <EditableToken path="photogallery.tabApartment" as="span">
              {tabLabels.apartment}
            </EditableToken>
          </button>
        </div>

        <EditableCollectionBinding
          bindingPath="photogallery.images"
          label="All Gallery Photos"
          className="photo-gallery__binding"
        >
          <EditableCollectionBinding
            bindingPath="photogallery.community"
            label="Community Photos"
            className="photo-gallery__binding-nested"
          >
            <EditableCollectionBinding
              bindingPath="photogallery.apartment"
              label="Apartment Photos"
              className="photo-gallery__binding-nested"
            >
              <EditableCollectionBinding
                bindingPath="photogallery.videos"
                label="Videos"
                className="photo-gallery__binding-nested"
              >
                {visibleItems.length === 0 ? (
                  <p className="photo-gallery__empty">Gallery images will be displayed here.</p>
                ) : (
                  <ul className="photo-gallery__grid">
                    {visibleItems.map((item, index) => (
                      <li key={item.key} className="photo-gallery__item">
                        <button
                          type="button"
                          className="photo-gallery__thumb"
                          onClick={() => setSelectedIndex(index)}
                          aria-label={`Open ${item.title || 'gallery item'}`}
                        >
                          {item.image ? (
                            <VibesitesImage
                              url={item.image}
                              alt={item.alt}
                              sizes="(min-width: 960px) 33vw, (min-width: 768px) 48vw, 100vw"
                              className="photo-gallery__img"
                            />
                          ) : (
                            <div className="photo-gallery__img-placeholder" aria-hidden="true" />
                          )}
                          {item.videoUrl ? (
                            <span className="photo-gallery__play" aria-hidden="true">
                              <svg viewBox="0 0 512 512" width="50" height="50" focusable="false">
                                <path
                                  fill="currentColor"
                                  d="M256 504c137 0 248-111 248-248S393 8 256 8 8 119 8 256s111 248 248 248zM40 256c0-118.7 96.1-216 216-216 118.7 0 216 96.1 216 216 0 118.7-96.1 216-216 216-118.7 0-216-96.1-216-216zm331.7-18l-176-107c-15.8-8.8-35.7 2.5-35.7 21v208c0 18.4 19.8 29.8 35.7 21l176-101c16.4-9.1 16.4-32.8 0-42zM192 335.8V176.9c0-4.7 5.1-7.6 9.1-5.1l134.5 81.7c3.9 2.4 3.8 8.1-.1 10.3L201 341c-4 2.3-9-.6-9-5.2z"
                                />
                              </svg>
                            </span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </EditableCollectionBinding>
            </EditableCollectionBinding>
          </EditableCollectionBinding>
        </EditableCollectionBinding>
      </div>

      <GalleryLightbox
        items={visibleItems}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <style>{`
        .photo-gallery {
          width: 100%;
          padding: 5rem 1.25rem;
        }
        .photo-gallery__inner {
          width: 100%;
          max-width: 1300px;
          margin: 0 auto;
          text-align: center;
        }
        .photo-gallery__tabs {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0;
          margin-bottom: 1.25rem;
        }
        .photo-gallery__tab {
          appearance: none;
          background: #444;
          color: #fff;
          border: none;
          border-radius: 0;
          padding: 0.95rem 1.9rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.02em;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .photo-gallery__tab:hover {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        .photo-gallery__tab.is-active {
          background: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
        }
        .photo-gallery__grid {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
        }
        .photo-gallery__item {
          flex: 0 0 33.333%;
          max-width: 33.333%;
          height: 300px;
          padding: 5px;
          box-sizing: border-box;
          overflow: hidden;
        }
        .photo-gallery__thumb {
          position: relative;
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          border: none;
          background: #111;
          cursor: pointer;
          overflow: hidden;
        }
        .photo-gallery__img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover;
          display: block;
        }
        .photo-gallery__img-placeholder {
          width: 100%;
          height: 100%;
          background: #e5e5e5;
        }
        .photo-gallery__play {
          position: absolute;
          inset: 0;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          pointer-events: none;
        }
        .photo-gallery__play svg {
          width: 50px;
          height: 50px;
          filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.45));
        }
        .photo-gallery__empty {
          margin: 2rem 0;
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          color: var(--vibe-color-text-muted);
        }
        .photo-gallery__binding,
        .photo-gallery__binding-nested {
          display: block;
        }

        .photo-gallery__lightbox {
          position: fixed;
          inset: 0;
          z-index: 10000;
          background: rgba(0, 0, 0, 0.92);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }
        .photo-gallery__lightbox-media {
          max-width: min(1100px, 92vw);
          max-height: 90vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .photo-gallery__lightbox-img {
          max-width: 100%;
          max-height: 90vh;
          width: auto;
          height: auto;
          object-fit: contain;
        }
        .photo-gallery__lightbox-video,
        .photo-gallery__lightbox-iframe {
          width: min(960px, 90vw);
          height: min(540px, 70vh);
          background: #000;
          border: none;
        }
        .photo-gallery__lightbox-close {
          position: absolute;
          top: 1rem;
          right: 1.25rem;
          background: transparent;
          border: none;
          color: #fff;
          font-size: 2.5rem;
          line-height: 1;
          cursor: pointer;
          opacity: 0.85;
        }
        .photo-gallery__lightbox-close:hover {
          opacity: 1;
        }
        .photo-gallery__lightbox-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #fff;
          font-size: 3rem;
          line-height: 1;
          cursor: pointer;
          padding: 0.5rem 0.75rem;
          opacity: 0.85;
        }
        .photo-gallery__lightbox-arrow:hover {
          opacity: 1;
        }
        .photo-gallery__lightbox-arrow--prev {
          left: 0.75rem;
        }
        .photo-gallery__lightbox-arrow--next {
          right: 0.75rem;
        }
        .photo-gallery__lightbox-counter {
          position: absolute;
          bottom: 1.25rem;
          left: 50%;
          transform: translateX(-50%);
          color: #fff;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          letter-spacing: 0.04em;
        }

        @media (max-width: 959px) {
          .photo-gallery__item {
            flex: 0 0 48%;
            max-width: 48%;
            height: 220px;
            margin: 5px;
            padding: 0;
          }
        }
        @media (max-width: 767px) {
          .photo-gallery {
            padding: 2.5rem 1rem;
          }
          .photo-gallery__tabs {
            flex-direction: column;
            align-items: stretch;
          }
          .photo-gallery__tab {
            width: 100%;
            margin: 2px 0;
          }
          .photo-gallery__item {
            flex: 0 0 100%;
            max-width: 100%;
            height: 220px;
            margin: 5px 0;
            padding: 0;
          }
          .photo-gallery__lightbox-arrow--prev {
            left: 0.25rem;
          }
          .photo-gallery__lightbox-arrow--next {
            right: 0.25rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PhotoGallery',
  displayName: 'Photo Gallery',
  tokens: {
    'photogallery.tabAll': {
      label: 'ALL Tab Label',
      type: 'text',
      defaultValue: 'ALL',
    },
    'photogallery.tabVideo': {
      label: 'Video Tab Label',
      type: 'text',
      defaultValue: 'Video',
    },
    'photogallery.tabCommunity': {
      label: 'Community Tab Label',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'photogallery.tabApartment': {
      label: 'Apartment Tab Label',
      type: 'text',
      defaultValue: 'Apartment Amenities',
    },
  },
  collectionBindings: {
    'photogallery.images': {
      label: 'All Gallery Photos',
      collection: 'photo_gallery',
      description: 'Full photo gallery used for the ALL tab and category fallbacks.',
    },
    'photogallery.community': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      description:
        'Photos whose title is exactly "Community". Loose title matching is used as a fallback.',
    },
    'photogallery.apartment': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      description:
        'Photos whose title is exactly "Apartment". Loose title matching is used as a fallback.',
    },
    'photogallery.videos': {
      label: 'Videos',
      collection: 'virtual_tours',
      description: 'Virtual tours / videos shown in the Video tab and ALL tab.',
    },
  },
};

(PhotoGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default PhotoGallery;
